import {
  APIComponentAttributes,
  APIComponentType,
  APICurrentUser,
  APIModelAttributes,
  APIPresentationAttributes,
  ArdoqId,
  EnhancedPresentation,
} from '@ardoq/api-types';
import { toByIdDictionary } from '@ardoq/common-helpers';
import { withBasePath } from 'basePath';
import { showToast, ToastType } from '@ardoq/status-ui';
import { t } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';

export const isOnlyContributor = (
  user: APICurrentUser,
  organizationLabel: string
) => {
  const orgRoles = user.roles.filter(
    role => role.split('/')[0] === organizationLabel
  );
  return orgRoles.every(role => role === `${organizationLabel}/contributor`);
};

export const extractPresentationDetailsFromUrl = (path: string) => {
  const match = path.match('/presentation/([^/]+)/([^/]+)');

  if (match === null) {
    return {
      org: null,
      presentationId: null,
    };
  } else if (match[2] === 'slide') {
    return {
      org: null,
      presentationId: match[1],
    };
  }
  return {
    org: match[1],
    presentationId: match[2],
  };
};

export const getPresentationURL = (presentationId: ArdoqId) =>
  withBasePath(['/presentation', presentationId].join('/'));

export const getPresentationSlideURL = (
  presentationId: ArdoqId,
  slideId: ArdoqId
): string =>
  withBasePath(['/presentation', presentationId, 'slide', slideId].join('/'));

export const copyPresentationURL = async (
  presentationId: ArdoqId
): Promise<void> => {
  // i18nLocale$.state OK here: imperative one-shot toast notification.
  const i18nLocale = i18nLocale$.state;
  try {
    await navigator.clipboard.writeText(
      `${window.location.origin}${getPresentationURL(presentationId)}`
    );
    showToast(
      t(i18nLocale, 'presentationAdmin:copyUrl.linkCopied', 'Link copied'),
      ToastType.SUCCESS
    );
  } catch {
    showToast(
      t(
        i18nLocale,
        'presentationAdmin:copyUrl.linkCopyFailed',
        'Could not copy link'
      ),
      ToastType.ERROR
    );
  }
};

export const openPresentationInNewTab = (url: string): void => {
  window.open(url, '_blank', 'noopener');
};

export const startPresentation = (presentationId: ArdoqId): void =>
  openPresentationInNewTab(getPresentationURL(presentationId));

export const startPresentationFromSlide = (
  presentationId: ArdoqId,
  slideId: ArdoqId
): void =>
  openPresentationInNewTab(getPresentationSlideURL(presentationId, slideId));

export const enhancePresentation = (
  presentation: APIPresentationAttributes
): EnhancedPresentation => {
  return {
    ...presentation,
    componentsById: toByIdDictionary(presentation.components),
  };
};

export const addOrgParamToImagePath = (
  imagePath: string,
  organization: string
) => {
  const imageUrl = new URL(imagePath, window.location.origin);
  if (imageUrl.searchParams.has('org')) return imagePath;
  imageUrl.searchParams.set('org', organization);
  return imageUrl.toString();
};

export const addOrgParamToComponentImagePath = (
  component: APIComponentAttributes,
  organization: string
) => {
  if (!component.image) return component;
  return {
    ...component,
    image: addOrgParamToImagePath(component.image, organization),
  };
};

export const addOrgParamToComponentTypeImagePaths = (
  componentType: APIComponentType,
  organization: string
): APIComponentType => {
  return {
    ...componentType,
    image: componentType.image
      ? addOrgParamToImagePath(componentType.image, organization)
      : componentType.image,
    children: addOrgParamToComponentTypesImagePaths(
      componentType.children,
      organization
    ),
  };
};

const addOrgParamToComponentTypesImagePaths = (
  componentTypes: Record<ArdoqId, APIComponentType>,
  organization: string
): Record<ArdoqId, APIComponentType> => {
  return Object.fromEntries(
    Object.entries(componentTypes).map(([typeId, type]) => {
      if (!type.image) return [typeId, type];
      return [typeId, addOrgParamToComponentTypeImagePaths(type, organization)];
    })
  );
};

export const addOrgParamToModelImagePaths = (
  model: APIModelAttributes,
  organization: string
): APIModelAttributes => {
  return {
    ...model,
    root: addOrgParamToComponentTypesImagePaths(model.root, organization),
  };
};

// For public presentations in organisation without a custom domain, the backend
// has no way of knowing which organization is requesting the image. This function
// transforms the image path to always contain the org parameter.
export const addOrgParamToPresentationImagePaths = (
  presentation: APIPresentationAttributes,
  organization: string | null
) => {
  if (!organization) return presentation;
  return {
    ...presentation,
    components: presentation.components.map(component =>
      addOrgParamToComponentImagePath(component, organization)
    ),
    models: presentation.models.map(model =>
      addOrgParamToModelImagePaths(model, organization)
    ),
  };
};
